<template>
    <row class="is-alternative-hotel" v-on:dblclick.native="openModal">
        <template slot="type">
            <i class="material-icons">local_hotel</i>
            <i class="material-icons" v-if="isDouble">local_hotel</i>
        </template>

        <template slot="name">
            <div class="alternative-hotel" v-on:click.stop>
                <span class="alternative-hotel__label">{{ label }}</span>
                <multiselect
                    v-if="isDouble"
                    class="alternative-hotel__select is-shorter"
                    v-on:dblclick.native.stop
                    v-model="selectedHotel"
                    :options="hotelOptions"
                    label="name"
                    track-by="requestId"
                    placeholder="Hotel auswählen"
                    :show-labels="false"
                />
            </div>
        </template>

        <template slot="marginTax">
            <margin-tax v-model="marginTaxEnabled" :tax="marginTax" :margin="grossMargin" />
        </template>

        <template slot="buyPrice">
            <div class="alternative-hotel__buy">
                <popover trigger="hover">
                    <i class="material-icons alternative-hotel__info">info_outline</i>
                    <template slot="popover">
                        <strong>Aufpreis für alle Nächte</strong><br>
                        ({{ altPerNight | priceView }} − {{ currentPerNight | priceView }})
                        × {{ nights }} {{ nights === 1 ? 'Nacht' : 'Nächte' }}<br>
                        = {{ buyPrice | priceView }}
                    </template>
                </popover>
                <input-price readOnly :value="buyPrice" />
            </div>
        </template>

        <template slot="sellPrice">
            <input-price v-model="sellPrice" />
        </template>

        <template slot="profit">
            <input-price v-model="profit" />
        </template>

        <template slot="margin">
            <input-price v-model="margin" />
        </template>

        <template slot="actions">
            <i class="material-icons" v-on:click.prevent.stop="deleteAlternative">delete</i>
        </template>
    </row>
</template>

<script>
import { Row, MarginTax } from '@calculations/components';
import InputPrice from '@calculations/components/InputPrice';
import { Multiselect } from '@components/form';
import Popover from '@components/Popover';
import { placeholderNights } from '@calculations/functions';
import { priceView } from '@utilities/functions';

export default {
    components: { Row, MarginTax, InputPrice, Multiselect, Popover },

    filters: { priceView },

    props: {
        alternative: {
            type: Object,
            required: true,
        },
        roomType: {
            type: String,
            required: true, // 'double' | 'single'
        },
    },

    computed: {
        isDouble: function () { return this.roomType === 'double' },

        sellField: function () {
            return this.isDouble ? 'doubleRoomSellPrice' : 'singleRoomPremiumSellPrice';
        },

        label: function () {
            var negative = Number(this.sellPrice) < 0;
            if (this.isDouble) {
                return negative
                    ? 'Preisminderung pro Person im DZ (gesamt für alle Nächte)'
                    : 'Aufpreis pro Person im DZ (für alle Nächte)';
            }
            return negative
                ? 'Verminderung des Einzelzimmerzuschlags'
                : 'Zusätzlicher Einzelzimmerzuschlag';
        },

        orderCurrency: function () {
            return this.$store.state.order.orderCurrency;
        },

        hotelOptions: function () {
            return this.$store.state.hotelRequestProviders;
        },

        // The chosen alternative hotel — selected via the multiselect on the DZ row.
        selectedHotel: {
            get: function () {
                return this.hotelOptions.find(option => option.requestId === this.alternative.requestId) || null;
            },
            set: function (value) {
                this.updatePrice({ requestId: value ? value.requestId : null });
            },
        },

        // HotelPrice of the placeholder the chosen alternative belongs to — reactive,
        // so editing that hotel's price in the calculation updates the surcharge live.
        placeholderHotelPrice: function () {
            if (!this.selectedHotel) {
                return null;
            }
            return this.$store.state.prices.hotelPrices
                .find(price => price.placeholder && price.placeholder.id === this.selectedHotel.placeholderId) || null;
        },

        nights: function () {
            const hotelPrice = this.placeholderHotelPrice;
            return hotelPrice && hotelPrice.placeholder ? placeholderNights(hotelPrice.placeholder) : 0;
        },

        // Per-night room price of the chosen alternative hotel.
        altPerNight: function () {
            if (!this.selectedHotel) {
                return 0;
            }
            return Number(this.isDouble ? this.selectedHotel.doublePrice : this.selectedHotel.singlePrice) || 0;
        },

        // Per-night room price of the placeholder's current hotel, without item prices — the
        // same base AlternativeHotelPrice::placeholderPricePerNight uses on the server.
        // doubleRoomBuyPrice is the total for all nights incl. item prices unless the hotel
        // is included, so it is only used (per night) when no calculation request is linked.
        currentPerNight: function () {
            const hotelPrice = this.placeholderHotelPrice;
            if (!hotelPrice) {
                return 0;
            }
            const placeholder = hotelPrice.placeholder || {};
            if (placeholder.calculationRequest) {
                const perNight = this.isDouble
                    ? hotelPrice.withoutItemPriceDoubleRoomBuyPrice
                    : hotelPrice.withoutItemPriceSingleRoomPremiumBuyPrice;
                return perNight && perNight.amount ? Number(perNight.amount) : 0;
            }
            const price = this.isDouble ? hotelPrice.doubleRoomBuyPrice : hotelPrice.singleRoomPremiumBuyPrice;
            const amount = price && price.amount ? Number(price.amount) : 0;
            const included = !placeholder.clientOffer || placeholder.clientOffer.status === 'included';
            return included || !this.nights ? amount : amount / this.nights;
        },

        // Surcharge for all nights — computed client-side so it reacts to both
        // the hotel selection and edits of the current hotel's price.
        buyPrice: function () {
            return (this.altPerNight - this.currentPerNight) * this.nights;
        },

        sellPrice: {
            get: function () {
                const price = this.alternative[this.sellField];
                return price && price.amount ? price.amount : 0;
            },
            set: function (value) {
                this.updatePrice({ [this.sellField]: { amount: value, currency: this.orderCurrency } });
            },
        },

        marginTaxEnabled: {
            get: function () { return this.alternative.marginTax },
            set: function (value) { this.updatePrice({ marginTax: value }) },
        },

        multiplier: function () {
            return this.marginTaxEnabled ? 1.19 : 1;
        },

        profit: {
            get: function () {
                return (Number(this.sellPrice) - this.buyPrice) / this.multiplier;
            },
            set: function (value) {
                this.sellPrice = this.buyPrice + (Number(value) * this.multiplier);
            },
        },

        margin: {
            get: function () {
                return this.buyPrice ? Number(this.profit) / this.buyPrice * 100 : 0;
            },
            set: function (value) {
                this.sellPrice = this.buyPrice * (1 + Number(value) / 100 * this.multiplier);
            },
        },

        marginTax: function () {
            return Number(this.profit) * 0.19;
        },

        grossMargin: function () {
            return this.buyPrice ? (Number(this.sellPrice) - this.buyPrice) / this.buyPrice : 0;
        },
    },

    methods: {
        updatePrice: function (obj = {}) {
            this.$store.commit('updateAlternativeHotelPrice', {
                key: this.alternative.key,
                ...obj,
            });
        },

        deleteAlternative: function () {
            this.$store.commit('removeAlternativeHotel', this.alternative.key);
        },

        openModal: function () {
            if (!this.selectedHotel || !this.selectedHotel.placeholderId) {
                return;
            }
            this.$store.commit('updateModalPlaceholder', {
                _type: 'hotel',
                placeholder: { id: this.selectedHotel.placeholderId },
            });
        },
    },
}
</script>
